/**
 * ASSISTED BOOKING WIDGET - Admin Dashboard KPI Card
 * ===================================================
 * Shows live operational stats for assisted bookings:
 * - Awaiting Payment count
 * - Paid Today count  
 * - Warning (>30min) / Critical (>2hr) stale counts
 * 
 * Clicking navigates to filtered pending list.
 * 
 * IMPORTANT: Read-only operations only. No payment bypass.
 * Created: 2026-04-07
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { 
  Shield, Clock, CheckCircle, AlertTriangle, AlertCircle, 
  RefreshCw, ExternalLink 
} from "lucide-react";

const AssistedBookingWidget = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get("/assisted-booking/stats");
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch assisted booking stats:", err);
      setError("Unable to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Refresh every 60 seconds
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleWidgetClick = () => {
    navigate("/admin/assisted-booking-list?filter=unpaid&sort=oldest");
  };

  const handleCreateNew = (e) => {
    e.stopPropagation();
    navigate("/admin/assisted-booking");
  };

  if (loading && !stats) {
    return (
      <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg p-4 animate-pulse">
        <div className="flex items-center gap-3 mb-3">
          <Shield className="h-5 w-5 text-amber-500" />
          <span className="text-slate-400 text-sm">Assisted Bookings</span>
        </div>
        <div className="h-8 bg-slate-700 rounded w-16 mb-2"></div>
        <div className="h-4 bg-slate-700 rounded w-24"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-900/50 border border-red-500/30 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-2">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
        <button 
          onClick={fetchStats}
          className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" /> Retry
        </button>
      </div>
    );
  }

  const hasWarning = stats?.warning_stale > 0;
  const hasCritical = stats?.critical_stale > 0;

  return (
    <div 
      onClick={handleWidgetClick}
      className={`cursor-pointer transition-all hover:scale-[1.02] rounded-lg p-4 border ${
        hasCritical 
          ? "bg-gradient-to-br from-red-500/20 to-orange-500/10 border-red-500/50 animate-pulse"
          : hasWarning
            ? "bg-gradient-to-br from-amber-500/20 to-orange-500/10 border-amber-500/40"
            : "bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30"
      }`}
      data-testid="assisted-booking-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber-500" />
          <span className="text-slate-300 text-sm font-medium">Assisted Bookings</span>
        </div>
        <button
          onClick={handleCreateNew}
          className="text-xs px-2 py-1 bg-amber-500 text-black rounded hover:bg-amber-400 font-medium"
          data-testid="widget-create-new"
        >
          + New
        </button>
      </div>

      {/* Main Stat: Awaiting Payment */}
      <div className="flex items-baseline gap-2 mb-3">
        <span className={`text-3xl font-bold ${
          hasCritical ? "text-red-400" : hasWarning ? "text-amber-400" : "text-white"
        }`}>
          {stats?.awaiting_payment || 0}
        </span>
        <span className="text-slate-400 text-sm">awaiting payment</span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {/* Paid Today */}
        <div className="bg-slate-800/50 rounded px-2 py-1.5">
          <div className="flex items-center gap-1 text-green-400">
            <CheckCircle className="h-3 w-3" />
            <span className="text-xs">Paid Today</span>
          </div>
          <span className="text-lg font-bold text-white">{stats?.paid_today || 0}</span>
        </div>

        {/* Warning (>30min) */}
        <div className={`rounded px-2 py-1.5 ${
          hasWarning ? "bg-amber-500/20" : "bg-slate-800/50"
        }`}>
          <div className={`flex items-center gap-1 ${hasWarning ? "text-amber-400" : "text-slate-400"}`}>
            <Clock className="h-3 w-3" />
            <span className="text-xs">&gt;30min</span>
          </div>
          <span className={`text-lg font-bold ${hasWarning ? "text-amber-400" : "text-white"}`}>
            {stats?.warning_stale || 0}
          </span>
        </div>

        {/* Critical (>2hr) */}
        <div className={`rounded px-2 py-1.5 ${
          hasCritical ? "bg-red-500/20" : "bg-slate-800/50"
        }`}>
          <div className={`flex items-center gap-1 ${hasCritical ? "text-red-400" : "text-slate-400"}`}>
            <AlertTriangle className="h-3 w-3" />
            <span className="text-xs">&gt;2hr</span>
          </div>
          <span className={`text-lg font-bold ${hasCritical ? "text-red-400" : "text-white"}`}>
            {stats?.critical_stale || 0}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>Created today: {stats?.created_today || 0}</span>
        <div className="flex items-center gap-1 text-amber-500">
          <span>View all</span>
          <ExternalLink className="h-3 w-3" />
        </div>
      </div>
    </div>
  );
};

export default AssistedBookingWidget;
